<script lang="ts" setup>
import { computed } from "vue";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import OmniCheckbox from "@/components/omni/OmniCheckbox.vue";
import OmniTooltip from "@/components/omni/OmniTooltip.vue";
import { CheckCallType } from "@/api/trips/dto/check-call";
import { AlertMessageType } from "@/constants/components";

const messageId = defineModel<string>({ required: true });
const error = defineModel<string | undefined>("error");
const saveWithoutUpdate = defineModel<boolean>("saveWithoutUpdate", { default: false });

const props = withDefaults(
  defineProps<{
    checkCallType: CheckCallType;
    initialMessageId: string;
    isLoaded: boolean;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const emit = defineEmits<{
  (e: "blur"): void;
}>();

const hasInitialMessageId = computed(() => Boolean(props.initialMessageId));

const isMessageIdChanged = computed(() => messageId.value !== props.initialMessageId);

const isMessageIdCleared = computed(() => hasInitialMessageId.value && !messageId.value);

const updateWord = computed(() => (saveWithoutUpdate.value ? "The next" : "This"));

const isFirstUpdate = computed(() => {
  return props.checkCallType === CheckCallType.dispatchConfirmed;
});

function onMessageIdUpdate(value: string) {
  messageId.value = value.trim();

  if (!value) {
    error.value = "";
  }
}
</script>

<template>
  <div class="message-id-field">
    <v-text-field
      :disabled="props.disabled"
      :error="Boolean(error)"
      :error-messages="error"
      :hide-details="!error"
      :model-value="messageId"
      autocomplete="off"
      class="mt-5"
      density="compact"
      label="Message ID"
      variant="outlined"
      @blur="emit('blur')"
      @update:model-value="onMessageIdUpdate"
    />

    <div v-if="error" class="mt-4"></div>

    <omni-tooltip :disabled="!isFirstUpdate">
      <template #activator>
        <omni-checkbox
          v-model="saveWithoutUpdate"
          :disabled="isFirstUpdate || props.disabled"
          class="mt-4"
          label="Save message ID without creating an update"
        ></omni-checkbox>
      </template>

      Saving a message ID without sending an update is available from the second update
    </omni-tooltip>

    <template v-if="!error && isLoaded">
      <alert-message v-if="isMessageIdCleared" :type="AlertMessageType.warning" class="mt-2">
        {{ updateWord }} update will start a new email chain
      </alert-message>

      <alert-message v-else-if="isMessageIdChanged && hasInitialMessageId" :type="AlertMessageType.warning" class="mt-2">
        {{ updateWord }} update will go to a different email chain
      </alert-message>

      <alert-message v-else-if="isMessageIdChanged" :type="AlertMessageType.info" class="mt-2">
        {{ updateWord }} update will go to the existing email chain
      </alert-message>

      <alert-message v-else-if="isFirstUpdate && hasInitialMessageId" :type="AlertMessageType.warning" class="mt-2">
        {{ updateWord }} update will go to the existing email chain of the order's message ID
      </alert-message>

      <alert-message v-else-if="isFirstUpdate" :type="AlertMessageType.info" class="mt-2">
        {{ updateWord }} update will start a new email chain
      </alert-message>
    </template>
  </div>
</template>
